import type {Connection,Variant} from '../../lib/types';
import {providerFormats,publicationCommentary,socialLinks,withLegacyLink,type Platform} from '@cap4/cap4publish-domain';

/** clientKey is selection identity; only id/version originate from the server. */
export type SocialVariant=Omit<Variant,'id'|'version'>&{clientKey:string;id?:string;version?:number};
export type SocialDraftValue={title:string;variants:SocialVariant[]};
export type SocialSaveInput={itemId:string;title:string;expectedVersion:number;operationId:string;variants:Array<Omit<Variant,'id'|'version'>&{id?:string;expectedVariantVersion:number|null}>};
export type SocialSaveResult={version:number;variants:Variant[]};
export type SocialAck={version:number;value:SocialDraftValue};
export type ConflictChoices={title:'local'|'server';variants:Record<string,'local'|'server'>};

export function fromContent(content:{title:string;variants?:Variant[]},previous?:SocialDraftValue):SocialDraftValue{
 return {title:content.title,variants:(content.variants||[]).map(v=>({...v,text:withLegacyLink(v.text,v.linkUrl),clientKey:previous?.variants.find(p=>p.connectionId===v.connectionId)?.clientKey||v.id,mediaIds:[...v.mediaIds]}))};
}

export function copyVariant(source:SocialVariant|undefined,target:{resourceId:string;connectionId:string},clientKey:string,mediaIds:string[]):SocialVariant{
 return {clientKey,...target,text:source?.text||'',linkUrl:source?.linkUrl,mediaIds:[...mediaIds]};
}

export function hasWritableExistingVariant(value:SocialDraftValue,writable:ReadonlySet<string>){
 return value.variants.some(v=>v.id&&writable.has(v.resourceId));
}

export function buildSocialSave(itemId:string,value:SocialDraftValue,version:number,writable:ReadonlySet<string>,operationId:string):SocialSaveInput{
 if(!hasWritableExistingVariant(value,writable))throw new Error('Du skal have skriveadgang til en eksisterende kanalvariant for at gemme eller tilføje en variant her.');
 return {itemId,title:value.title,expectedVersion:version,operationId,variants:value.variants.filter(v=>writable.has(v.resourceId)).map(v=>({
  ...(v.id?{id:v.id}:{}),resourceId:v.resourceId,connectionId:v.connectionId,text:v.text,linkUrl:v.linkUrl,mediaIds:[...v.mediaIds],expectedVariantVersion:v.id?v.version??null:null,
 }))};
}

/** Ack represents the snapshot actually accepted, including idempotent replay. */
export function acknowledgeSocialDraft(submitted:SocialDraftValue,result:SocialSaveResult):SocialDraftValue{
 return {...submitted,variants:submitted.variants.map(v=>{const saved=result.variants.find(s=>s.connectionId===v.connectionId);return {...v,...(saved?{id:saved.id,version:saved.version}:{}),mediaIds:[...v.mediaIds]}})};
}

export function reconcileSocialAck(current:SocialDraftValue,_submitted:SocialDraftValue,acknowledged:SocialDraftValue):SocialDraftValue{
 return {...current,variants:current.variants.map(v=>{const saved=acknowledged.variants.find(s=>s.connectionId===v.connectionId);return saved?.id?{...v,id:saved.id,version:saved.version}:v})};
}

/** A timeout is not evidence of failure. Keep exact payload and operation until resolved. */
export function createSocialSaver(itemId:string,writable:()=>ReadonlySet<string>,send:(input:SocialSaveInput)=>Promise<SocialSaveResult>,nextOperation:()=>string){
 let pending:{snapshot:SocialDraftValue;input:SocialSaveInput}|undefined;
 const save=async(value:SocialDraftValue,version:number):Promise<SocialAck>=>{
  if(!pending){const snapshot=structuredClone(value);pending={snapshot,input:buildSocialSave(itemId,snapshot,version,writable(),nextOperation())}}
  const attempt=pending;
  try{const result=await send(attempt.input);pending=undefined;return {version:result.version,value:acknowledgeSocialDraft(attempt.snapshot,result)}}
  catch(error){
   // These responses prove the transaction was rejected. Network/unknown failures may have committed.
   const code=(error as {code?:string})?.code;
   if(code&&['conflict','forbidden','unauthorized','validation','invalid','invalid_input','not_found'].includes(code))pending=undefined;
   throw error;
  }
 };
 return Object.assign(save,{hasUnresolvedSave:()=>!!pending});
}

/** All selections use fresh server metadata for the next compare-and-swap. */
export function resolveSocialConflict(local:SocialDraftValue,server:SocialDraftValue,choices:ConflictChoices,writable:ReadonlySet<string>):SocialDraftValue{
 const variants:SocialVariant[]=server.variants.map(remote=>{
  const mine=local.variants.find(v=>v.connectionId===remote.connectionId);
  if(!mine||!writable.has(remote.resourceId)||choices.variants[mine.clientKey]!=='local')return remote;
  return {...mine,id:remote.id,version:remote.version,resourceId:remote.resourceId,mediaIds:[...mine.mediaIds]};
 });
 for(const mine of local.variants){
  if(!server.variants.some(v=>v.connectionId===mine.connectionId)&&writable.has(mine.resourceId)&&choices.variants[mine.clientKey]==='local')variants.push({...mine,id:undefined,version:undefined,mediaIds:[...mine.mediaIds]});
 }
 return {title:choices.title==='local'?local.title:server.title,variants};
}

export function socialCommentary(variant:SocialVariant,connection?:Pick<Connection,'platform'>){
 return ['linkedin','facebook'].includes(connection?.platform||'')?publicationCommentary(variant.text,variant.linkUrl,variant.mediaIds.length>0):variant.text;
}

type FormatConnection=Pick<Connection,'platform'|'maxCharacters'|'maxImages'>&Partial<Pick<Connection,'capabilities'>>;
export function socialFormats(connection?:FormatConnection){
 const defaults=providerFormats[connection?.platform as Platform]||{text:false,link:false,images:0,maxCharacters:null};
 const formats=connection?.capabilities?.formats;
 const reported=typeof formats==='object'?formats:undefined;
 return {text:reported?.text??defaults.text,link:reported?.link??defaults.link,images:reported?.images??connection?.maxImages??defaults.images,maxCharacters:reported?.maxCharacters!==undefined?reported.maxCharacters:connection?.maxCharacters!==undefined?connection.maxCharacters:defaults.maxCharacters};
}
/** The text is authoritative; attachment metadata follows the first inline URL. */
export function socialTextEdit(text:string,connection?:FormatConnection){
 return {text,linkUrl:socialFormats(connection).link?socialLinks(text)[0]?.url:undefined};
}
export function validateSocialVariant(variant:SocialVariant,connection?:FormatConnection):string|undefined{
 const formats=socialFormats(connection);
 if(connection?.platform==='instagram'&&!variant.mediaIds.length)return 'Instagram kræver mindst ét billede.';
 if(variant.text.trim()&&!formats.text&&connection?.platform!=='instagram')return 'Kanalen understøtter ikke tekstopslag.';
 if(variant.linkUrl&&!formats.link)return 'Kanalen understøtter ikke links.';
 if(variant.linkUrl){try{if(!['http:','https:'].includes(new URL(variant.linkUrl).protocol))throw new Error()}catch{return 'Linket skal være en gyldig http- eller https-adresse.'}}
 if(variant.mediaIds.length>formats.images)return `Kanalen tillader højst ${formats.images} billeder. Fjern selv de overskydende billeder.`;
 if(formats.maxCharacters!==null&&Array.from(socialCommentary(variant,connection)).length>formats.maxCharacters)return 'Teksten og et eventuelt vedhæftet link overskrider kanalens tegngrænse. Forkort teksten før publicering.';
 if(!variant.text.trim()&&!variant.linkUrl&&!variant.mediaIds.length)return 'Skriv en tekst, tilføj et link eller et billede før publicering.';
 return undefined;
}
